import React, {
  useMemo,
  useState,
  useRef,
  useEffect,
  useCallback,
} from 'react';
import PropTypes from 'prop-types';
import { MotifTable } from '@ey-xd/motif-react';
import {
  handleFilterChanged,
  handleGridReady,
  handleItemsPerPageChange,
} from '../../../common/AgGridEventHandlers';
import CustomPagination from '../../../common/MotifPagination';
import CustomHeaderTemplate from '../../shared/customHeaders/CustomHeaderTemplate';
import MasterDataManagementSecuritiesCellRenderer from '../../shared/customRenderers/MasterDataManagementSecuritiesCellRenderer';
import DateComparator from '../../shared/customComparators/DateComparator';
import DateCellRenderer from '../../checklistDeliveryCalendar/customRenderers/DateCellRenderer';
import * as formatters from '../../../scripts/formFormatters';
const SecuritiesTable = ({
  securitiesData,
  handleEditButtonClick,
  handleDeleteButtonClick,
  handleGridChanges,
  gridColumnState,
  refreshGridSecurityData,
}) => {
  const gridRef = useRef();
  const [rowData, setRowData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(100);

  const dateOptions = {
    day: 'numeric',
    month: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  };

  //Default column settings
  const defaultColDef = useMemo(() => {
    return {
      flex: 1,
      minWidth: 65,
      filter: true,
      resizable: true,
      sortable: true,
      wrapText: true,
      autoHeight: true,
    };
  }, []);

  // Instruction text for the overlay
  const instructionText =
    'Click on Actions > Click Export Security Import Template > Update the downloaded file with the security details > Click Upload Security Import Template.';
  const getValueOrNA = (params, valueKey, formatDate = false) => {
    if (params.data?.isSystemGenerated) {
      return 'N/A'; // Display 'N/A' for system-generated records
    }
    const value = params.data?.[valueKey] || null;
    return formatDate && value ? formatters.formatDate(value) : value;
  };

  // Memoize column definitions to prevent unnecessary re-renders
  const columnDefs = useMemo(() => {
    return [
      {
        field: 'isSystemGenerated',
        headerName: 'System Created',
        sortable: true,
        showRowGroup: false,
        hide: false,
        rowGroup: false,
        minWidth: 160,
        valueGetter: (params) => {
          return params.data?.isSystemGenerated ? 'Yes' : 'No';
        },
        headerComponentParams: {
          template: CustomHeaderTemplate('SystemCreated'),
        },
      },
      {
        field: 'reitSuiteEntityIDForREIT',
        headerName: 'REITSuite Entity ID for REIT',
        sortable: true,
        showRowGroup: false,
        hide: false,
        rowGroup: false,
        minWidth: 220,
        valueGetter: (params) => {
          return params.data?.reitSuiteEntityIDForREIT || '';
        },
        headerComponentParams: {
          template: CustomHeaderTemplate('REITSuiteEntityIDforREIT'),
        },
      },
      {
        field: 'reitShortName',
        headerName: 'REIT Name',
        sortable: true,
        showRowGroup: false,
        hide: false,
        rowGroup: false,
        minWidth: 200,
        valueGetter: (params) => {
          return params.data?.reitShortName || '';
        },
        headerComponentParams: { template: CustomHeaderTemplate('REITName') },
      },
      {
        field: 'isActive',
        headerName: 'REIT Status',
        sortable: true,
        showRowGroup: false,
        hide: false,
        rowGroup: false,
        minWidth: 155,
        valueGetter: (params) => {
          return params.data?.isActive ? 'Active' : 'Inactive';
        },
        headerComponentParams: { template: CustomHeaderTemplate('REITStatus') },
      },
      {
        field: 'reitSuiteEntityIDForProperty',
        headerName: 'REITSuite Entity ID for Property',
        sortable: true,
        showRowGroup: false,
        hide: false,
        rowGroup: false,
        minWidth: 235,
        valueGetter: (params) => {
          return params.data?.reitSuiteEntityIDForProperty || '';
        },
        headerComponentParams: {
          template: CustomHeaderTemplate('REITSuiteEntityIDforProperty'),
        },
      },
      {
        field: 'propertyName',
        headerName: 'Property Name',
        sortable: true,
        showRowGroup: false,
        hide: false,
        rowGroup: false,
        minWidth: 250,
        valueGetter: (params) => {
          return params.data?.propertyName || '';
        },
        headerComponentParams: {
          template: CustomHeaderTemplate('PropertyName'),
        },
      },
      {
        field: 'chartName',
        headerName: 'Chart Name',
        sortable: true,
        showRowGroup: false,
        hide: false,
        rowGroup: false,
        minWidth: 300,
        valueGetter: (params) => {
          return params.data?.chartName || '';
        },
        headerComponentParams: { template: CustomHeaderTemplate('ChartName') },
      },
      {
        field: 'subAttribute',
        headerName: 'Sub Attribute',
        sortable: true,
        showRowGroup: false,
        hide: false,
        rowGroup: false,
        minWidth: 300,
        valueGetter: (params) => {
          return params.data?.subAttribute || '';
        },
        headerComponentParams: {
          template: CustomHeaderTemplate('SubAttribute'),
        },
      },
      {
        field: 'securityName',
        headerName: 'Security Name',
        sortable: true,
        showRowGroup: false,
        hide: false,
        rowGroup: false,
        minWidth: 350,
        valueGetter: (params) => {
          return params.data?.securityName || '';
        },
        headerComponentParams: {
          template: CustomHeaderTemplate('SecurityName'),
        },
      },
      {
        field: 'acquisitionDate',
        headerName: 'Acquisition Date',
        sortable: true,
        showRowGroup: false,
        hide: false,
        rowGroup: false,
        minWidth: 181,
        valueGetter: (params) => getValueOrNA(params, 'acquisitionDate', true),
        headerComponentParams: {
          template: CustomHeaderTemplate('AcquisitionDate'),
        },
      },
      {
        field: 'initialQuarter',
        headerName: 'Initial Quarter',
        sortable: true,
        showRowGroup: false,
        hide: false,
        rowGroup: false,
        minWidth: 170,
        valueGetter: (params) => {
          return params.data?.initialQuarter || '';
        },
        headerComponentParams: {
          template: CustomHeaderTemplate('InitialQuarter'),
        },
      },
      {
        field: 'endDate',
        headerName: 'End Date',
        sortable: true,
        showRowGroup: false,
        hide: false,
        rowGroup: false,
        minWidth: 150,
        valueGetter: (params) => getValueOrNA(params, 'endDate', true),
        headerComponentParams: { template: CustomHeaderTemplate('EndDate') },
      },
      {
        field: 'finalQuarter',
        headerName: 'Final Quarter',
        sortable: true,
        showRowGroup: false,
        hide: false,
        rowGroup: false,
        minWidth: 170,
        valueGetter: (params) => getValueOrNA(params, 'finalQuarter'),
        headerComponentParams: {
          template: CustomHeaderTemplate('FinalQuarter'),
        },
      },

      {
        field: 'issuer',
        headerName: 'Issuer',
        sortable: true,
        showRowGroup: false,
        hide: false,
        rowGroup: false,
        minWidth: 350,
        valueGetter: (params) => {
          return params.data?.issuer || '';
        },
        headerComponentParams: { template: CustomHeaderTemplate('Issuer') },
      },
      {
        field: 'class',
        headerName: 'Class',
        sortable: true,
        showRowGroup: false,
        hide: false,
        rowGroup: false,
        minWidth: 350,
        valueGetter: (params) => {
          return params.data?.class || '';
        },
        headerComponentParams: { template: CustomHeaderTemplate('Class') },
      },
      {
        field: 'lastUpdatedBy',
        headerName: 'Last Updated By',
        sortable: true,
        minWidth: 185,
        hide: true,
        headerComponentParams: {
          template: CustomHeaderTemplate('LastUpdatedBy'),
        },
      },
      {
        field: 'lastUpdatedDateTime',
        headerName: 'Last Updated Date',
        sortable: true,
        minWidth: 200,
        hide: true,
        cellRenderer: DateCellRenderer,
        cellRendererParams: { dateFieldName: 'lastUpdatedDateTime' },
        filterValueGetter: (params) => {
          const dateValue =
            params.data && params.data.lastUpdatedDateTime
              ? params.data.lastUpdatedDateTime
              : null;
          return dateValue
            ? new Date(dateValue + 'Z').toLocaleDateString('en-US', dateOptions)
            : '';
        },
        comparator: DateComparator,
        headerComponentParams: {
          template: CustomHeaderTemplate('LastUpdatedDate'),
        },
      },
      {
        field: 'editactions',
        headerName: '',
        suppressMenu: true,
        suppressHeaderFilterButton: true,
        suppressSeparator: true,
        sortable: false,
        minWidth: 70,
        maxWidth: 70,
        cellRenderer: MasterDataManagementSecuritiesCellRenderer,
        cellRendererParams: { handleEditButtonClick },
      },
      {
        field: 'deleteactions',
        headerName: '',
        suppressMenu: true,
        suppressHeaderFilterButton: true,
        suppressSeparator: true,
        sortable: false,
        minWidth: 70,
        maxWidth: 70,
        cellRenderer: MasterDataManagementSecuritiesCellRenderer,
        cellRendererParams: { handleDeleteButtonClick },
      },
    ];
  }, [handleEditButtonClick, handleDeleteButtonClick, dateOptions]);

  useEffect(() => {
    if (rowData?.length > 0) {
      // Handle pagination calculations
      let calcTotalPages =
        gridRef &&
        gridRef.current &&
        gridRef.current.api &&
        gridRef.current.api.paginationGetTotalPages();
      if (calcTotalPages > 0) {
        setTotalPages(calcTotalPages);
        setCurrentPage(1);
      } else {
        setTotalPages(1);
        setCurrentPage(1);
      }
    }
    // Handle grid changes
    handleGridChanges(
      rowData,
      gridRef && gridRef.current ? gridRef.current.api : null
    );
  }, [rowData]);
  useEffect(() => {
    setRowData(securitiesData);
    if (gridRef && gridRef.current && gridRef.current.api) {
      // Get total pages from Grid's default pagination control and apply it to custom pagination control
      let calcTotalPages = gridRef.current.api.paginationGetTotalPages();
      setTotalPages(calcTotalPages);
    }
  }, [securitiesData]);

  useEffect(() => {
    setRowData(securitiesData);
  }, [securitiesData]);

  useEffect(() => {
    //Reset Grid to default column state
    //When Grid Column state changes due to Reset Column State action then Reset Grid to default column state
    let localStorageColumnState = localStorage.getItem(
      'REITSuiteSecurityDataColumnState'
    );
    if (
      !localStorageColumnState &&
      gridRef &&
      gridRef.current &&
      gridRef.current.api
    ) {
      gridRef.current.api.setColumnDefs([]);
      gridRef.current.api.setColumnDefs(columnDefs);
    }
  }, [gridColumnState]);

  //Handle First Data Renderered event
  const handleFirstDataRenderedEvent = useCallback((event) => {
    gridRef.current.columnApi?.autoSizeAllColumns();
  }, []);

  //Handle Grid Page change event
  const handlePageChange = useCallback((val) => {
    setCurrentPage(val);
    gridRef.current.api.paginationGoToPage(val - 1);
  }, []);
  const rowHeight = 73;
  const headerHeight = 45;
  const totalHeight = 5 * rowHeight + headerHeight; // set 5 rows as default

  const overlayNoRowsTemplate = `<div class="custom-no-rows-template">${instructionText}</div>`;
  const hasData = securitiesData && securitiesData.length > 0;

  const onCellKeyDown = useCallback(
    (event) => {
      if (event.event.key === 'Enter' || event.event.key === ' ') {
        if (event.colDef.field == 'editactions') {
          handleEditButtonClick(event.data);
        }
        if (!event.data.isCannotBeDeleted) {
          if (event.colDef.field == 'deleteactions') {
            handleDeleteButtonClick(event.data);
          }
        }
      }
    },
    [handleEditButtonClick, handleDeleteButtonClick]
  );
  return (
    <React.Fragment>
      <div className="row mb-10">
        <div className="col mt-3">
          <div
            data-testid="grdSecuritiesDataGrid"
            className={`ag-theme-quartz motif-table ${hasData ? 'table-bd-full-height-width' : 'empty-table-container'}`}
          >
            <MotifTable
              zebra={true}
              compact={true}
              rowData={rowData}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              accentedSort={true}
              rowSelection="multiple"
              suppressRowClickSelection={true}
              onGridReady={handleGridReady}
              onFirstDataRendered={handleFirstDataRenderedEvent}
              ref={gridRef}
              pagination={true}
              suppressPaginationPanel={true}
              paginationPageSize={100}
              groupDisplayType={'custom'}
              groupSelectsChildren={true}
              groupDefaultExpanded={0}
              onFilterChanged={handleFilterChanged(
                gridRef,
                currentPage,
                setCurrentPage,
                setTotalPages
              )}
              overlayNoRowsTemplate={overlayNoRowsTemplate}
              onCellKeyDown={onCellKeyDown}
            />

            {rowData?.length > 0 && (
              <div className="mt-3">
                <span className="right mb-3">
                  <CustomPagination
                    currentPage={currentPage}
                    handlePageChange={handlePageChange}
                    totalPages={totalPages}
                    handleItemsPerPageChange={handleItemsPerPageChange}
                    gridRef={gridRef}
                    setCurrentPage={setCurrentPage}
                    setItemsPerPage={setItemsPerPage}
                    setTotalPages={setTotalPages}
                  />
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </React.Fragment>
  );
};

SecuritiesTable.propTypes = {
  securitiesData: PropTypes.array.isRequired,
  handleEditButtonClick: PropTypes.func.isRequired,
  handleDeleteButtonClick: PropTypes.func.isRequired,
  handleGridChanges: PropTypes.func.isRequired,
  gridColumnState: PropTypes.object.isRequired,
  refreshGridSecurityData: PropTypes.func.isRequired,
};

export default SecuritiesTable;
